import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import {
  ArrowLeft,
  History,
  Loader2,
  Printer,
  Search as SearchIcon,
  RefreshCw,
  Trash2,
  Sliders,
  Plus,
  Copy,
  AlertTriangle,
  FileText,
  Info,
  Tag,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDateTime } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useAssets, useLookups, type AssetListFilters, type AssetListRow } from "@/features/assets/api";
import { ASSET_CLASSES, ASSET_STATUSES, ASSET_TYPES, ALLOCATION_STATUSES } from "@/features/assets/types";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canPrintQrLabels } from "@/lib/permissions";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked, isDeptScopedOnly } from "@/lib/roles";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { PermissionDenied } from "@/components/auth/PermissionDenied";

import {
  PrintSheet,
  TEMPLATE_LABELS,
  PAPER_LABELS,
  PAPER_SIZES,
  totalPages,
  labelsPerPage,
  defaultConfigForTemplate,
  defaultThermalConfig,
  useQrDataUrls,
  type Columns,
  type LabelTemplate,
  type PaperSize,
  type QrSize,
} from "@/features/assets/qr-bulk/PrintSheet";
import {
  useRecordPrintBatch,
  useQrPrintBatches,
  useQrBatchAssets,
  useAssetsByIds,
  useQrLabelTemplates,
  useDeleteQrLabelTemplate,
  useDuplicateQrLabelTemplate,
  THERMAL_PRESETS,
  type ThermalPreset,
  type PrintMode,
  type AssetLabelTemplateItem,
  type QrLabelTemplateConfig,
} from "@/features/assets/qr-bulk/api";
import { CustomizeTemplateDialog } from "@/features/assets/qr-bulk/CustomizeTemplateDialog";
import { isBuildingAsset, isVehicleAsset } from "@/features/assets/classification";

const searchSchema = z.object({
  selected: z.string().optional(),
  batch: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/assets/qr-labels")({
  validateSearch: (s: Record<string, unknown>) => searchSchema.parse(s),
  component: QrLabelsPage,
});

type Scope =
  | "selected"
  | "branch"
  | "department"
  | "building"
  | "floor"
  | "location"
  | "asset_class"
  | "asset_type"
  | "asset_category"
  | "newly_added"
  | "without_qr"
  | "custom";

const SCOPE_LABELS: Record<Scope, string> = {
  selected: "Selected Assets",
  branch: "Branch",
  department: "Department",
  building: "Building",
  floor: "Floor",
  location: "Room / Location",
  asset_class: "Asset Class",
  asset_type: "Asset Type",
  asset_category: "Asset Category",
  newly_added: "Newly Added (last 30 days)",
  without_qr: "Assets Without QR Printed",
  custom: "Custom Filter",
};

const ALL = "__all__";

type QrPrinted = "all" | "printed" | "not_printed";

type LocalFilters = {
  scope: Scope;
  branchId: string | null;
  departmentId: string | null;
  building: string | null; // building_name
  floor: string | null;
  locationId: string | null;
  assetClass: string | null;
  assetType: string | null;
  categoryId: string | null;
  status: string | null;
  allocationStatus: string | null;
  qrPrinted: QrPrinted;
  purchaseFrom: string;
  purchaseTo: string;
  createdFrom: string;
  createdTo: string;
  search: string;
};

const emptyFilters = (branchId: string | null): LocalFilters => ({
  scope: "custom",
  branchId,
  departmentId: null,
  building: null,
  floor: null,
  locationId: null,
  assetClass: null,
  assetType: null,
  categoryId: null,
  status: null,
  allocationStatus: null,
  qrPrinted: "all",
  purchaseFrom: "",
  purchaseTo: "",
  createdFrom: "",
  createdTo: "",
  search: "",
});

function QrLabelsPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/assets/qr-labels" });
  const { data: me } = useCurrentUser();
  const roles = me?.roles ?? [];
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = isBranchLocked(roles)
    ? (me?.profile?.branch_id ?? null)
    : (lockedBranchId ?? currentBranchId ?? null);

  const [filters, setFilters] = useState<LocalFilters>(() =>
    emptyFilters(effectiveBranchId),
  );
  
  const [activeTab, setActiveTab] = useState(search.batch ? "history" : "print");

  // Handoff from Asset Registry with checked IDs.
  const preselectIds = useMemo(() => {
    if (!search.selected) return [] as string[];
    return search.selected.split(",").filter(Boolean);
  }, [search.selected]);

  useEffect(() => {
    if (preselectIds.length > 0) {
      setFilters((f) => ({ ...f, scope: "selected" }));
    }
  }, [preselectIds.length]);

  // Sync branch context if the user has a branch lock/switch.
  useEffect(() => {
    setFilters((f) => (f.branchId === effectiveBranchId ? f : { ...f, branchId: effectiveBranchId }));
  }, [effectiveBranchId]);

  if (!canPrintQrLabels(roles)) {
    return <PermissionDenied />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-1 -ml-2">
            <Link to="/assets">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to Asset Registry
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Print QR Labels</h1>
          <p className="text-sm text-muted-foreground">
            Choose a print scope, refine assets, pick a template, then print.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="print">
            <Printer className="mr-1 h-4 w-4" /> New Print Batch
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="mr-1 h-4 w-4" /> History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="print">
          <NewBatchTab
            filters={filters}
            setFilters={setFilters}
            preselectIds={preselectIds}
            roles={roles}
            effectiveBranchId={effectiveBranchId}
          />
        </TabsContent>
        <TabsContent value="history">
          <HistoryTab
            focusBatchId={search.batch ?? null}
            onReprint={(assetIds, template) => {
              // Jump back to Print tab with these IDs preselected via query.
              navigate({
                to: "/assets/qr-labels",
                search: { selected: assetIds.join(",") },
              });
              setFilters((f) => ({ ...f, scope: "selected" }));
              setActiveTab("print");
              // template will be picked up by user in Step C.
              void template;
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- New batch (scope → filters → preview → template → print) ----------

function NewBatchTab({
  filters,
  setFilters,
  preselectIds,
  roles,
  effectiveBranchId,
}: {
  filters: LocalFilters;
  setFilters: React.Dispatch<React.SetStateAction<LocalFilters>>;
  preselectIds: string[];
  roles: import("@/lib/roles").AppRole[];
  effectiveBranchId: string | null;
}) {
  void roles;
  const { data: lookups } = useLookups();

  // Building / floor pickers pull distinct values from asset table via query
  // in the assets list result. For MVP we take building names from locations
  // (kind='building') and floor from a free text input.
  const buildings = useMemo(
    () => (lookups?.locations ?? []).filter((l) => l.kind === "building"),
    [lookups],
  );
  const rooms = useMemo(
    () => (lookups?.locations ?? []).filter((l) => l.kind === "room"),
    [lookups],
  );
  const depts = useMemo(
    () =>
      (lookups?.departments ?? []).filter(
        (d) => !filters.branchId || d.branch_id === filters.branchId,
      ),
    [lookups, filters.branchId],
  );

  // Translate LocalFilters + Scope into an AssetListFilters query.
  const listFilters: AssetListFilters = useMemo(() => {
    const f: AssetListFilters = {
      page: 1,
      pageSize: 500,
      branchId: filters.branchId,
      includeInStore: true, // labels are printed for assets kept in store as well as allocated ones
    };
    if (filters.scope === "selected") return f; // ids-based, not filter based
    if (filters.scope === "department" && filters.departmentId) f.departmentId = filters.departmentId;
    if (filters.scope === "asset_class" && filters.assetClass) f.assetClass = filters.assetClass;
    if (filters.scope === "asset_type" && filters.assetType) f.assetType = filters.assetType;
    if (filters.scope === "asset_category" && filters.categoryId) f.categoryId = filters.categoryId;
    if (filters.scope === "location" && filters.locationId) {
      // Not a first-class filter on useAssets — fallback client filter below.
    }
    // Common filters always applied
    if (filters.departmentId && filters.scope !== "department") f.departmentId = filters.departmentId;
    if (filters.categoryId && filters.scope !== "asset_category") f.categoryId = filters.categoryId;
    if (filters.assetClass && filters.scope !== "asset_class") f.assetClass = filters.assetClass;
    if (filters.assetType && filters.scope !== "asset_type") f.assetType = filters.assetType;
    if (filters.status) f.status = filters.status;
    if (filters.allocationStatus) f.allocationStatus = filters.allocationStatus;
    if (filters.search.trim()) f.search = filters.search.trim();
    return f;
  }, [filters]);

  // Feed by IDs when scope=selected, otherwise via filter query.
  const idsQuery = useAssetsByIds(filters.scope === "selected" ? preselectIds : []);
  const filterQuery = useAssets(filters.scope === "selected" ? { pageSize: 0, page: 1 } : listFilters);

  const rawRows: AssetListRow[] = useMemo(() => {
    if (filters.scope === "selected") return idsQuery.data ?? [];
    return filterQuery.data?.rows ?? [];
  }, [filters.scope, idsQuery.data, filterQuery.data]);

  // Client-side post filters (building/floor/location/qr-printed/dates/newly).
  const rows = useMemo(() => {
    const now = Date.now();
    return rawRows.filter((a) => {
      if (filters.scope === "newly_added") {
        const created = a.created_at ? new Date(a.created_at).getTime() : 0;
        if (now - created > 30 * 24 * 3600 * 1000) return false;
      }
      if (filters.scope === "without_qr") {
        const count = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
        if (count > 0 || a.qr_printed_at) return false;
      }
      if (filters.qrPrinted === "printed") {
        const count = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
        if (count === 0 && !a.qr_printed_at) return false;
      }
      if (filters.qrPrinted === "not_printed") {
        const count = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
        if (count > 0 || a.qr_printed_at) return false;
      }
      if (filters.building) {
        const bn = (a.building_name ?? "").toLowerCase();
        const target = filters.building.toLowerCase();
        if (!bn.includes(target)) return false;
      }
      if (filters.floor) {
        const floor = String(a.number_of_floors ?? "");
        if (!floor.includes(filters.floor)) return false;
      }
      if (filters.locationId && a.location?.id !== filters.locationId) return false;
      if (filters.purchaseFrom) {
        const inv = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
        if (inv < new Date(filters.purchaseFrom).getTime()) return false;
      }
      if (filters.purchaseTo) {
        const inv = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
        if (inv > new Date(filters.purchaseTo).getTime()) return false;
      }
      if (filters.createdFrom) {
        const c = a.created_at ? new Date(a.created_at).getTime() : 0;
        if (c < new Date(filters.createdFrom).getTime()) return false;
      }
      if (filters.createdTo) {
        const c = a.created_at ? new Date(a.created_at).getTime() : 0;
        if (c > new Date(filters.createdTo).getTime()) return false;
      }
      return true;
    });
  }, [rawRows, filters]);

  const loading = filters.scope === "selected" ? idsQuery.isLoading : filterQuery.isLoading;

  // ----- Preview selection -----
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [previewSearch, setPreviewSearch] = useState("");

  // Auto-check everything the first time rows land.
  useEffect(() => {
    setChecked(new Set(rows.map((r) => r.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map((r) => r.id).join("|")]);

  const previewRows = useMemo(() => {
    const q = previewSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.asset_code.toLowerCase().includes(q) || (r.name ?? "").toLowerCase().includes(q),
    );
  }, [rows, previewSearch]);

  const selectedRows = useMemo(() => rows.filter((r) => checked.has(r.id)), [rows, checked]);

  const allChecked = previewRows.length > 0 && previewRows.every((r) => checked.has(r.id));
  const toggleAll = () => {
    const next = new Set(checked);
    if (allChecked) previewRows.forEach((r) => next.delete(r.id));
    else previewRows.forEach((r) => next.add(r.id));
    setChecked(next);
  };
  const toggleOne = (id: string) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChecked(next);
  };
  const removeFromBatch = (id: string) => {
    const next = new Set(checked);
    next.delete(id);
    setChecked(next);
  };

  // ----- Templates / dynamic templates -----
  const templatesQuery = useQrLabelTemplates();
  const templates = templatesQuery.data ?? [];
  const deleteTemplateMut = useDeleteQrLabelTemplate();
  const duplicateTemplateMut = useDuplicateQrLabelTemplate();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<AssetLabelTemplateItem | null>(null);

  const [printMode, setPrintMode] = useState<PrintMode>("sheet");
  const [columns, setColumns] = useState<Columns>(2);
  const [qrSize, setQrSize] = useState<QrSize>("md");
  const [paperSize, setPaperSize] = useState<PaperSize>("A4");

  // Auto-select initial template once templates load
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      const standard = templates.find((t) => t.name.toLowerCase().includes("standard")) || templates[0];
      setSelectedTemplateId(standard.id);
      const mode: PrintMode =
        standard.configuration.print_mode || (standard.configuration.paper_size === "Thermal" ? "thermal" : "sheet");
      setPrintMode(mode);
      if (mode === "thermal") {
        setPaperSize("Thermal");
        setColumns(1);
      } else {
        setPaperSize(standard.configuration.paper_size || "A4");
        setColumns((standard.configuration.columns as Columns) || 2);
      }
      setQrSize(standard.configuration.qr_size || "md");
    }
  }, [templates, selectedTemplateId]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  const activeConfig = useMemo<QrLabelTemplateConfig>(() => {
    const isThermal = printMode === "thermal";
    if (selectedTemplate) {
      return {
        ...selectedTemplate.configuration,
        print_mode: printMode,
        paper_size: isThermal ? "Thermal" : paperSize,
        columns: isThermal ? 1 : columns,
        qr_size: qrSize,
      };
    }
    const base = isThermal
      ? defaultThermalConfig("50x30")
      : defaultConfigForTemplate("standard");
    return {
      ...base,
      print_mode: printMode,
      paper_size: isThermal ? "Thermal" : paperSize,
      columns: isThermal ? 1 : columns,
      qr_size: qrSize,
    };
  }, [selectedTemplate, printMode, paperSize, columns, qrSize]);

  const onSelectTemplate = (id: string) => {
    setSelectedTemplateId(id);
    const found = templates.find((t) => t.id === id);
    if (found) {
      const mode: PrintMode =
        found.configuration.print_mode || (found.configuration.paper_size === "Thermal" ? "thermal" : "sheet");
      setPrintMode(mode);
      if (mode === "thermal") {
        setPaperSize("Thermal");
        setColumns(1);
      } else {
        setPaperSize(found.configuration.paper_size || "A4");
        setColumns((found.configuration.columns as Columns) || 2);
      }
      setQrSize(found.configuration.qr_size || "md");
    }
  };

  const handleDuplicateCurrent = async () => {
    if (!selectedTemplate) return;
    try {
      const dup = await duplicateTemplateMut.mutateAsync(selectedTemplate.id);
      toast.success(`Duplicated as "${dup.name}"`);
      setSelectedTemplateId(dup.id);
    } catch (e) {
      toast.error((e as Error).message || "Failed to duplicate template.");
    }
  };

  const handleDeleteTemplate = async (item: AssetLabelTemplateItem) => {
    if (item.is_system_template) {
      toast.error("System templates cannot be deleted.");
      return;
    }
    if (!confirm(`Are you sure you want to delete template "${item.name}"?`)) return;
    try {
      await deleteTemplateMut.mutateAsync(item.id);
      toast.success(`Template "${item.name}" deleted.`);
      const firstSys = templates.find((t) => t.is_system_template && t.id !== item.id);
      if (firstSys) onSelectTemplate(firstSys.id);
    } catch (e) {
      toast.error((e as Error).message || "Failed to delete template.");
    }
  };

  // Auto-suggest template when all selected are vehicles or buildings.
  useEffect(() => {
    if (selectedRows.length === 0 || templates.length === 0) return;
    if (selectedRows.every(isBuildingAsset)) {
      const bldg = templates.find((t) => t.name.toLowerCase().includes("building"));
      if (bldg && selectedTemplateId !== bldg.id) onSelectTemplate(bldg.id);
    } else if (selectedRows.every(isVehicleAsset)) {
      const veh = templates.find((t) => t.name.toLowerCase().includes("vehicle"));
      if (veh && selectedTemplateId !== veh.id) onSelectTemplate(veh.id);
    }
  }, [selectedRows, templates]);

  // ----- Print + log -----
  const recordMut = useRecordPrintBatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastPrintedIds, setLastPrintedIds] = useState<string[] | null>(null);

  // QR readiness: ensure all canvas/PNG URLs are rendered before triggering print
  const { isReady: isQrReady } = useQrDataUrls(selectedRows);

  const doPrint = () => {
    if (selectedRows.length === 0) {
      toast.error("Select at least one asset");
      return;
    }
    if (!isQrReady) {
      toast.info("Preparing high-resolution QR codes, please wait a moment...");
      return;
    }
    setLastPrintedIds(selectedRows.map((r) => r.id));
    setTimeout(() => {
      window.print();
      setConfirmOpen(true);
    }, 100);
  };

  const markPrinted = async (ok: boolean) => {
    setConfirmOpen(false);
    if (!ok || !lastPrintedIds || lastPrintedIds.length === 0) return;
    try {
      await recordMut.mutateAsync({
        assetIds: lastPrintedIds,
        template: selectedTemplate ? selectedTemplate.name : "standard",
        scope: filters.scope,
        scopeMeta: {
          ...buildScopeMeta(filters),
          printMode,
          paperSize: printMode === "thermal" ? "Thermal" : paperSize,
          columns: printMode === "thermal" ? 1 : columns,
          qrSize,
          labelWidthMm: activeConfig.label_width_mm,
          labelHeightMm: activeConfig.label_height_mm,
          templateId: selectedTemplateId,
        },
        branchId: filters.branchId ?? effectiveBranchId,
      });
      toast.success(`Logged ${lastPrintedIds.length} label${lastPrintedIds.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error((e as Error).message || "Could not save print log");
    }
  };

  return (
    <div className="space-y-4">
      {/* Step A — Scope + filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Scope &amp; Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <Label className="text-xs">Print Scope</Label>
              <Select
                value={filters.scope}
                onValueChange={(v) => setFilters((f) => ({ ...f, scope: v as Scope }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(SCOPE_LABELS) as Scope[]).map((k) => (
                    <SelectItem
                      key={k}
                      value={k}
                      disabled={k === "selected" && preselectIds.length === 0}
                    >
                      {SCOPE_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FilterSelect
              label="Branch"
              value={filters.branchId}
              onChange={(v) => setFilters((f) => ({ ...f, branchId: v }))}
              options={(lookups?.branches ?? []).map((b) => ({ value: b.id, label: b.name }))}
              disabled={isBranchLocked((useCurrentUser().data?.roles ?? []))}
            />
            <FilterSelect
              label="Department"
              value={filters.departmentId}
              onChange={(v) => setFilters((f) => ({ ...f, departmentId: v }))}
              options={depts.map((d) => ({ value: d.id, label: d.name }))}
            />
            <FilterSelect
              label="Category"
              value={filters.categoryId}
              onChange={(v) => setFilters((f) => ({ ...f, categoryId: v }))}
              options={(lookups?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <FilterSelect
              label="Building (by location)"
              value={filters.building}
              onChange={(v) => setFilters((f) => ({ ...f, building: v }))}
              options={buildings.map((b) => ({ value: b.name, label: b.name }))}
            />
            <div>
              <Label className="text-xs">Floor</Label>
              <Input
                value={filters.floor ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, floor: e.target.value || null }))
                }
                placeholder="e.g. 2"
              />
            </div>
            <FilterSelect
              label="Room / Location"
              value={filters.locationId}
              onChange={(v) => setFilters((f) => ({ ...f, locationId: v }))}
              options={rooms.map((r) => ({ value: r.id, label: r.name }))}
            />
            <FilterSelect
              label="Asset Class"
              value={filters.assetClass}
              onChange={(v) => setFilters((f) => ({ ...f, assetClass: v }))}
              options={ASSET_CLASSES.map((c) => ({ value: c, label: c }))}
            />
            <FilterSelect
              label="Asset Type"
              value={filters.assetType}
              onChange={(v) => setFilters((f) => ({ ...f, assetType: v }))}
              options={ASSET_TYPES.map((t) => ({ value: t, label: t }))}
            />
            <FilterSelect
              label="Asset Status"
              value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              options={ASSET_STATUSES.map((s) => ({ value: s, label: s }))}
            />
            <FilterSelect
              label="Allocation Status"
              value={filters.allocationStatus}
              onChange={(v) => setFilters((f) => ({ ...f, allocationStatus: v }))}
              options={ALLOCATION_STATUSES.map((s) => ({ value: s, label: s }))}
            />
            <FilterSelect
              label="QR Printed"
              value={filters.qrPrinted === "all" ? null : filters.qrPrinted}
              onChange={(v) =>
                setFilters((f) => ({ ...f, qrPrinted: (v as QrPrinted) ?? "all" }))
              }
              options={[
                { value: "printed", label: "Printed" },
                { value: "not_printed", label: "Not Printed" },
              ]}
              allLabel="All"
            />
            <div>
              <Label className="text-xs">Purchase From</Label>
              <Input
                type="date"
                value={filters.purchaseFrom}
                onChange={(e) => setFilters((f) => ({ ...f, purchaseFrom: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Purchase To</Label>
              <Input
                type="date"
                value={filters.purchaseTo}
                onChange={(e) => setFilters((f) => ({ ...f, purchaseTo: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Created From</Label>
              <Input
                type="date"
                value={filters.createdFrom}
                onChange={(e) => setFilters((f) => ({ ...f, createdFrom: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Created To</Label>
              <Input
                type="date"
                value={filters.createdTo}
                onChange={(e) => setFilters((f) => ({ ...f, createdTo: e.target.value }))}
              />
            </div>
          </div>
          <div className="relative max-w-md">
            <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search asset code or name…"
              className="pl-8"
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFilters(emptyFilters(effectiveBranchId))}
            >
              <RefreshCw className="mr-1 h-3 w-3" /> Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step B — Preview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">2. Preview</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative w-56">
              <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={previewSearch}
                onChange={(e) => setPreviewSearch(e.target.value)}
                placeholder="Filter within preview…"
                className="pl-8"
              />
            </div>
            <Badge variant="secondary">
              {selectedRows.length} of {rows.length} selected
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
                </TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Building / Floor / Room</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>QR Printed</TableHead>
                <TableHead>Last Printed</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : previewRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center text-muted-foreground">
                    No assets match the current scope &amp; filters.
                  </TableCell>
                </TableRow>
              ) : (
                previewRows.map((a) => {
                  const printedCount = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
                  const printed = printedCount > 0 || !!a.qr_printed_at;
                  return (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Checkbox
                          checked={checked.has(a.id)}
                          onCheckedChange={() => toggleOne(a.id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{a.asset_code}</TableCell>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell><Badge variant="secondary">{a.asset_class ?? "Equipment"}</Badge></TableCell>
                      <TableCell>{a.asset_type ?? "—"}</TableCell>
                      <TableCell>{a.department?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">
                        {[a.building_name, a.number_of_floors, a.location?.name]
                          .filter(Boolean)
                          .join(" / ") || "—"}
                      </TableCell>
                      <TableCell>{a.status}</TableCell>
                      <TableCell>
                        {printed ? (
                          <Badge>Yes</Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {a.qr_printed_at ? fmtDateTime(a.qr_printed_at) : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFromBatch(a.id)}
                          title="Remove from batch"
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Step C — Template + Print */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">3. Template &amp; Print</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to filters
            </Button>
            <ActionGuard can={selectedRows.length > 0}>
              <Button size="sm" onClick={doPrint} disabled={!isQrReady}>
                {!isQrReady ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Printer className="mr-1 h-4 w-4" />
                )}
                {printMode === "thermal" ? "Print Thermal Roll" : "Print Sheet"} ({selectedRows.length})
              </Button>
            </ActionGuard>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Printing Mode Switcher */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-neutral-50/70 dark:bg-neutral-900/70 rounded-lg border">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                {printMode === "thermal" ? (
                  <>
                    <Printer className="h-3.5 w-3.5 text-primary" /> Thermal Label Roll Printing Mode
                  </>
                ) : (
                  <>
                    <FileText className="h-3.5 w-3.5 text-primary" /> Multi-Column Sheet Printing Mode
                  </>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {printMode === "thermal"
                  ? "Direct single-label roll feed (Zebra, TSC, Dymo, Brother, Xprinter, etc.) — 1 label per page feed"
                  : "A4 / A3 / Letter multi-column sheets for standard office laser/inkjet printers"}
              </div>
            </div>

            <div className="flex items-center gap-1 bg-white dark:bg-neutral-950 p-1 rounded-md border shadow-2xs">
              <Button
                type="button"
                variant={printMode === "sheet" ? "default" : "ghost"}
                size="sm"
                className="h-7 text-xs px-2.5"
                onClick={() => {
                  setPrintMode("sheet");
                  if (paperSize === "Thermal") setPaperSize("A4");
                  if (columns === 1) setColumns(2);
                }}
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" /> Sheet (A4)
              </Button>
              <Button
                type="button"
                variant={printMode === "thermal" ? "default" : "ghost"}
                size="sm"
                className="h-7 text-xs px-2.5"
                onClick={() => {
                  setPrintMode("thermal");
                  setPaperSize("Thermal");
                  setColumns(1);
                  if (qrSize === "lg" && activeConfig.label_height_mm <= 26) setQrSize("sm");
                }}
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Thermal Roll
              </Button>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Template</Label>
                {selectedTemplate?.is_system_template ? (
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">System</Badge>
                ) : selectedTemplate ? (
                  <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-4">Custom</Badge>
                ) : null}
              </div>
              <Select
                value={selectedTemplateId ?? ""}
                onValueChange={onSelectTemplate}
              >
                <SelectTrigger><SelectValue placeholder="Select template..." /></SelectTrigger>
                <SelectContent>
                  {templates.filter((t) => t.is_system_template).length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                        System Templates
                      </div>
                      {templates.filter((t) => t.is_system_template).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {templates.filter((t) => !t.is_system_template).length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-t mt-1 pt-1">
                        Custom Templates
                      </div>
                      {templates.filter((t) => !t.is_system_template).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {printMode === "sheet" ? (
              <>
                <div>
                  <Label className="text-xs">Paper</Label>
                  <Select value={paperSize} onValueChange={(v) => setPaperSize(v as PaperSize)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PAPER_LABELS) as PaperSize[])
                        .filter((k) => k !== "Thermal")
                        .map((k) => (
                          <SelectItem key={k} value={k}>{PAPER_LABELS[k]}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Columns</Label>
                  <Select value={String(columns)} onValueChange={(v) => setColumns(Number(v) as Columns)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Column</SelectItem>
                      <SelectItem value="2">2 Columns</SelectItem>
                      <SelectItem value="3">3 Columns</SelectItem>
                      <SelectItem value="4">4 Columns</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Media Type</Label>
                  <div className="h-9 px-3 border rounded-md bg-neutral-50 dark:bg-neutral-900 flex items-center text-xs text-muted-foreground">
                    {activeConfig.thermal_media_type === "continuous"
                      ? "Continuous Feed (No Gap)"
                      : `Die-Cut Roll (${activeConfig.label_gap_mm ?? 3}mm Gap)`}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Label Size</Label>
                  <div className="h-9 px-3 border rounded-md bg-neutral-50 dark:bg-neutral-900 flex items-center text-xs font-mono font-medium">
                    {activeConfig.label_width_mm} × {activeConfig.label_height_mm} mm
                  </div>
                </div>
              </>
            )}

            <div>
              <Label className="text-xs">QR size</Label>
              <Select value={qrSize} onValueChange={(v) => setQrSize(v as QrSize)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm">Small (18mm — compact)</SelectItem>
                  <SelectItem value="md">Medium (26mm — standard)</SelectItem>
                  <SelectItem value="lg">Large (34mm — high visibility)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Template Actions Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-y py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTemplateToEdit(selectedTemplate);
                  setCustomModalOpen(true);
                }}
              >
                <Sliders className="mr-1.5 h-3.5 w-3.5 text-primary" />
                {selectedTemplate?.is_system_template ? "Customize Template" : "Edit Template"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTemplateToEdit(null);
                  setCustomModalOpen(true);
                }}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New Template
              </Button>
              {selectedTemplate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDuplicateCurrent}
                  disabled={duplicateTemplateMut.isPending}
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Duplicate
                </Button>
              )}
              {selectedTemplate && !selectedTemplate.is_system_template && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 h-8 px-2"
                  onClick={() => handleDeleteTemplate(selectedTemplate)}
                  disabled={deleteTemplateMut.isPending}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
            </div>

            <div className="text-xs text-muted-foreground">
              Dimensions: <strong>{activeConfig.label_width_mm} × {activeConfig.label_height_mm} mm</strong> · Font: <strong>{activeConfig.font_size_pt} pt</strong>
            </div>
          </div>

          {/* Thermal Printer Browser Setup Tips */}
          {printMode === "thermal" && (
            <div className="p-3 bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 rounded-lg text-xs space-y-1 text-blue-900 dark:text-blue-200">
              <div className="font-semibold flex items-center gap-1.5">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                <span>Thermal Printer Browser Setup Tips</span>
              </div>
              <p className="text-[11px] leading-relaxed text-blue-950/80 dark:text-blue-300/90">
                In your browser print dialog: <strong>1.</strong> Select your thermal printer (Zebra, TSC, Dymo, Brother, Xprinter, etc.). <strong>2.</strong> Set Paper Size to match your roll (<strong>{activeConfig.label_width_mm} × {activeConfig.label_height_mm} mm</strong> or <em>User Defined</em>). <strong>3.</strong> Set Margins to <strong>None (0 mm)</strong> to ensure exact physical alignment.
              </p>
            </div>
          )}

          {/* Physical Constraint & Overflow Warnings */}
          {printMode === "sheet" && (() => {
            const paper = PAPER_SIZES[paperSize] || PAPER_SIZES.A4;
            const usableW = paper.w - 16;
            const gap = 4;
            const totalGridW = columns * activeConfig.label_width_mm + (columns - 1) * gap;
            const isWidthExceeded = totalGridW > usableW;

            if (isWidthExceeded) {
              return (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-md text-xs flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                  <div>
                    <strong>Layout Exceeds Printable Width:</strong> {columns} columns × {activeConfig.label_width_mm}mm + gaps = {totalGridW}mm, but usable {paperSize} width is only {usableW}mm. Please reduce columns or customize label width.
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {printMode === "thermal" && activeConfig.label_height_mm <= 26 && qrSize !== "sm" && (
            <div className="p-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-md text-xs flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <div>
                <strong>QR Size Notice:</strong> On compact thermal labels ({activeConfig.label_height_mm}mm height), Medium and Large QR codes may cause vertical overflow. Switch QR size to <strong>Small (18mm)</strong> for best fit.
              </div>
            </div>
          )}

          {/* Fit Stats */}
          {printMode === "sheet" ? (
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-normal bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                {labelsPerPage(columns, qrSize, paperSize, activeConfig.label_height_mm)} labels fit per {paperSize} sheet
              </Badge>
              <span>·</span>
              <span>
                {selectedRows.length} total label{selectedRows.length === 1 ? "" : "s"} to print
              </span>
              <span>·</span>
              <span>
                Requires {totalPages(selectedRows.length, columns, qrSize, paperSize, activeConfig.label_height_mm)} {paperSize} sheet{totalPages(selectedRows.length, columns, qrSize, paperSize, activeConfig.label_height_mm) === 1 ? "" : "s"}
              </span>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-normal bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-200">
                1 label per feed · Zero page margins
              </Badge>
              <span>·</span>
              <span>
                {selectedRows.length} total thermal label{selectedRows.length === 1 ? "" : "s"} to feed sequentially
              </span>
              <span>·</span>
              <span>
                Roll size: {activeConfig.label_width_mm} × {activeConfig.label_height_mm} mm
              </span>
            </div>
          )}

          {/* Print Sheet Preview Area */}
          <div className="rounded border bg-neutral-100 dark:bg-neutral-900 p-3 overflow-auto">
            {selectedRows.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Select assets in step 2 to preview labels.
              </div>
            ) : (
              <PrintSheet
                assets={selectedRows}
                templateConfig={activeConfig}
                columns={columns}
                qrSize={qrSize}
                paperSize={paperSize}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <CustomizeTemplateDialog
        open={customModalOpen}
        onOpenChange={setCustomModalOpen}
        templateToEdit={templateToEdit}
        sampleAsset={selectedRows[0] || previewRows[0] || null}
        onTemplateSaved={(saved) => {
          setSelectedTemplateId(saved.id);
          const mode: PrintMode =
            saved.configuration.print_mode || (saved.configuration.paper_size === "Thermal" ? "thermal" : "sheet");
          setPrintMode(mode);
          setPaperSize(mode === "thermal" ? "Thermal" : (saved.configuration.paper_size || "A4"));
          setColumns(mode === "thermal" ? 1 : ((saved.configuration.columns as Columns) || 2));
          setQrSize(saved.configuration.qr_size || "md");
        }}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Were labels printed successfully?</AlertDialogTitle>
            <AlertDialogDescription>
              Confirming will log this batch and mark {lastPrintedIds?.length ?? 0} asset
              {(lastPrintedIds?.length ?? 0) === 1 ? "" : "s"} as printed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => markPrinted(false)}>No, skip</AlertDialogCancel>
            <AlertDialogAction onClick={() => markPrinted(true)}>Yes, mark as printed</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- History tab ----------

function HistoryTab({
  focusBatchId,
  onReprint,
}: {
  focusBatchId: string | null;
  onReprint: (assetIds: string[], template: LabelTemplate) => void;
}) {
  const { data: batches, isLoading } = useQrPrintBatches();
  const [openBatch, setOpenBatch] = useState<string | null>(focusBatchId);

  useEffect(() => {
    setOpenBatch(focusBatchId);
  }, [focusBatchId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Print Batch History</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch</TableHead>
              <TableHead>Printed At</TableHead>
              <TableHead>Printed By</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead className="text-right">Labels</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : (batches ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                  No print batches yet.
                </TableCell>
              </TableRow>
            ) : (
              (batches ?? []).map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="font-mono text-xs">{b.id.slice(0, 8)}</TableCell>
                  <TableCell>{fmtDateTime(b.created_at)}</TableCell>
                  <TableCell>{b.printed_by?.full_name ?? b.printed_by?.email ?? "—"}</TableCell>
                  <TableCell>{SCOPE_LABELS[b.scope as Scope] ?? b.scope}</TableCell>
                  <TableCell>{b.template}</TableCell>
                  <TableCell>{b.branch?.name ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{b.label_count}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setOpenBatch(b.id)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {openBatch && (
          <BatchAssetsDialog
            batchId={openBatch}
            onClose={() => setOpenBatch(null)}
            onReprint={(ids) => {
              const b = (batches ?? []).find((x) => x.id === openBatch);
              onReprint(ids, (b?.template as LabelTemplate) ?? "standard");
              setOpenBatch(null);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function BatchAssetsDialog({
  batchId,
  onClose,
  onReprint,
}: {
  batchId: string;
  onClose: () => void;
  onReprint: (assetIds: string[]) => void;
}) {
  const { data: logs } = useQrBatchAssets(batchId);
  const ids = useMemo(() => (logs ?? []).map((l) => l.asset_id), [logs]);
  const { data: assets } = useAssetsByIds(ids);

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Batch assets</AlertDialogTitle>
          <AlertDialogDescription>
            {(assets ?? []).length} asset{(assets ?? []).length === 1 ? "" : "s"} in this batch.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-80 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Department</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(assets ?? []).map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono text-xs">{a.asset_code}</TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{a.department?.name ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction onClick={() => onReprint(ids)}>Reprint batch</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------- helpers ----------

function FilterSelect({
  label,
  value,
  onChange,
  options,
  disabled,
  allLabel = "All",
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  allLabel?: string;
}) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select
        value={value ?? ALL}
        onValueChange={(v) => onChange(v === ALL ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger><SelectValue placeholder={allLabel} /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function buildScopeMeta(f: LocalFilters): Record<string, unknown> {
  const meta: Record<string, unknown> = {};
  if (f.branchId) meta.branchId = f.branchId;
  if (f.departmentId) meta.departmentId = f.departmentId;
  if (f.categoryId) meta.categoryId = f.categoryId;
  if (f.building) meta.building = f.building;
  if (f.floor) meta.floor = f.floor;
  if (f.locationId) meta.locationId = f.locationId;
  if (f.assetClass) meta.assetClass = f.assetClass;
  if (f.assetType) meta.assetType = f.assetType;
  if (f.status) meta.status = f.status;
  if (f.allocationStatus) meta.allocationStatus = f.allocationStatus;
  return meta;
}
